import apiService from '../apiService';

// ============================================
// ORDER HELPERS WITH API INTEGRATION
// ============================================

/**
 * Create order from cart
 * @param {Object} orderData - Order details
 * @returns {Promise<Object>} Order creation result
 */
export const createOrder = async (orderData) => {
  if (!apiService.isAuthenticated()) {
    return { 
      success: false, 
      message: 'Please login to place order' 
    };
  }

  try {
    const response = await apiService.createOrder(orderData);

    if (response.success) {
      // Clear cart after successful order
      localStorage.removeItem('cart');
      localStorage.removeItem('cartItems');
      localStorage.removeItem('cartItemCount');
      localStorage.removeItem('orders');
    
      return {
        success: true,
        order: response.data.order,
        message: 'Order placed successfully!'
      };
    }

    return {
      success: false,
      message: response.data?.message || 'Failed to place order'
    };
  } catch (error) {
    console.error('Error creating order:', error);
    return {
      success: false,
      message: 'Failed to place order. Please try again.'
    };
  }
};

/**
 * Get user's order history with pagination
 * @param {Number} page - Page number (default: 1)
 * @param {Number} perPage - Items per page (default: 10)
 * @param {Object} filters - Optional filters (status, date range)
 * @returns {Promise<Object>} Orders with pagination metadata
 */
export const getMyOrders = async (page = 1, perPage = 10, filters = {}) => {
  console.log('🔍 getMyOrders called:', { page, perPage, filters, isAuthenticated: apiService.isAuthenticated() });
  
  if (!apiService.isAuthenticated()) {
    // Return empty result with pagination
    const localOrders = localStorage.getItem('orders');
    const orders = localOrders ? JSON.parse(localOrders) : [];
    console.log('⚠️ Not authenticated, returning local orders:', orders.length);
    return {
      orders: orders,
      pagination: {
        current_page: 1,
        per_page: perPage,
        total: orders.length,
        total_pages: Math.ceil(orders.length / perPage)
      }
    };
  }

  try {
    console.log('📡 Calling API getMyOrders with pagination...');
    const response = await apiService.getMyOrders(page, perPage, filters);
    console.log('📥 Raw API Response:', response);
    console.log('📦 Response structure:', {
      success: response.success,
      hasData: !!response.data,
      hasOrders: !!response.data?.orders,
      ordersType: typeof response.data?.orders,
      ordersLength: response.data?.orders?.data?.length
    });
    
    if (response.success && response.data.orders) {
      const ordersData = response.data.orders.data || response.data.orders;
      const pagination = {
        current_page: response.data.orders.current_page || page,
        per_page: response.data.orders.per_page || perPage,
        total: response.data.orders.total || ordersData.length,
        total_pages: response.data.orders.last_page || Math.ceil(ordersData.length / perPage),
        from: response.data.orders.from || 1,
        to: response.data.orders.to || ordersData.length
      };
      
      console.log('✅ Orders found:', ordersData.length, 'orders on page', page);
      console.log('📄 Pagination:', pagination);
      console.log('📋 First order sample:', ordersData[0]);
      
      // Cache orders locally (only first page)
      if (page === 1) {
        localStorage.setItem('orders', JSON.stringify(ordersData));
      }
      
      return {
        orders: ordersData,
        pagination: pagination
      };
    }

    // Fallback to local storage
    console.log('⚠️ No orders in response, checking localStorage...');
    const localOrders = localStorage.getItem('orders');
    const orders = localOrders ? JSON.parse(localOrders) : [];
    return {
      orders: orders,
      pagination: {
        current_page: 1,
        per_page: perPage,
        total: orders.length,
        total_pages: Math.ceil(orders.length / perPage)
      }
    };
  } catch (error) {
    console.error('❌ Error fetching orders:', error);
    console.error('Error details:', {
      message: error.message,
      stack: error.stack
    });
    
    // Fallback to local storage
    const localOrders = localStorage.getItem('orders');
    const orders = localOrders ? JSON.parse(localOrders) : [];
    return {
      orders: orders,
      pagination: {
        current_page: 1,
        per_page: perPage,
        total: orders.length,
        total_pages: Math.ceil(orders.length / perPage)
      }
    };
  }
};

/**
 * Get single order details
 * @param {number} orderId - Order ID
 * @returns {Promise<Object>} Order details
 */
export const getOrderById = async (orderId) => {
  if (!apiService.isAuthenticated()) {
    return { success: false, message: 'Please login to view order details' };
  }

  try {
    const response = await apiService.getOrderById(orderId);

    if (response.success && response.data.order) {
      return {
        success: true,
        order: response.data.order
      };
    }

    return {
      success: false,
      message: 'Order not found'
    };
  } catch (error) {
    console.error('Error fetching order details:', error);
    return {
      success: false,
      message: 'Failed to fetch order details'
    };
  }
};

/**
 * Track order status
 * @param {number} orderId - Order ID
 * @returns {Promise<Object>} Order tracking info
 */
export const trackOrder = async (orderId) => {
  if (!apiService.isAuthenticated()) {
    return { success: false, message: 'Please login to track order' };
  }

  try {
    const response = await apiService.trackOrder(orderId);

    if (response.success && response.data.tracking) {
      return {
        success: true,
        tracking: response.data.tracking
      };
    }

    return {
      success: false,
      message: 'Tracking information not available'
    };
  } catch (error) {
    console.error('Error tracking order:', error);
    return {
      success: false,
      message: 'Failed to fetch tracking information'
    };
  }
};

/**
 * Cancel order
 * @param {number} orderId - Order ID
 * @param {string} reason - Cancellation reason
 * @returns {Promise<Object>} Cancellation result
 */
export const cancelOrder = async (orderId, reason = '') => {
  if (!apiService.isAuthenticated()) {
    return { success: false, message: 'Please login to cancel order' };
  }

  try {
    const response = await apiService.cancelOrder(orderId, reason);

    if (response.success) {
      // Update local orders cache
      const localOrders = localStorage.getItem('orders');
      if (localOrders) {
        const orders = JSON.parse(localOrders);
        const updatedOrders = orders.map(order =>
          order.id === orderId ? { ...order, status: 'Cancelled' } : order
        );
        localStorage.setItem('orders', JSON.stringify(updatedOrders));
      }

      return {
        success: true,
        message: 'Order cancelled successfully'
      };
    }

    return {
      success: false,
      message: response.data?.message || 'Failed to cancel order'
    };
  } catch (error) {
    console.error('Error cancelling order:', error);
    return {
      success: false,
      message: 'Failed to cancel order. Please try again.'
    };
  }
};

/**
 * Rate/Review order
 * @param {number} orderId - Order ID
 * @param {Object} ratingData - Rating and review data
 * @returns {Promise<Object>} Rating submission result
 */
export const rateOrder = async (orderId, ratingData) => {
  if (!apiService.isAuthenticated()) {
    return { success: false, message: 'Please login to rate order' };
  }

  if (!ratingData.rating || ratingData.rating < 1 || ratingData.rating > 5) {
    return { success: false, message: 'Please provide a valid rating (1-5)' };
  }

  try {
    const response = await apiService.rateOrder(orderId, {
      rating: ratingData.rating,
      review: ratingData.review || '',
      productId: ratingData.productId
    });

    if (response.success) {
      return {
        success: true,
        message: 'Thank you for your feedback!'
      };
    }

    return {
      success: false,
      message: response.data?.message || 'Failed to submit rating'
    };
  } catch (error) {
    console.error('Error submitting rating:', error);
    return {
      success: false,
      message: 'Failed to submit rating. Please try again.'
    };
  }
};

/**
 * Reorder - Add items from previous order to cart
 * @param {number} orderId - Order ID to reorder
 * @returns {Promise<Object>} Reorder result
 */
export const reorder = async (orderId) => {
  if (!apiService.isAuthenticated()) {
    return { success: false, message: 'Please login to reorder' };
  }

  try {
    // Get order details
    const orderResponse = await getOrderById(orderId);

    if (!orderResponse.success || !orderResponse.order) {
      return {
        success: false,
        message: 'Order not found'
      };
    }

    const order = orderResponse.order;

    // Add each item to cart
    const addPromises = order.items.map(item =>
      apiService.addToCart({
        id: item.product_id,
        quantity: item.quantity,
        price: item.price,
        name: item.product_name
      })
    );

    await Promise.all(addPromises);

    return {
      success: true,
      message: 'Items added to cart successfully!'
    };
  } catch (error) {
    console.error('Error reordering:', error);
    return {
      success: false,
      message: 'Failed to reorder. Please try again.'
    };
  }
};

/**
 * Get order statistics
 * @returns {Promise<Object>} Order stats
 */
export const getOrderStats = async () => {
  const orders = await getMyOrders();

  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'Pending').length,
    processing: orders.filter(o => o.status === 'Processing').length,
    delivered: orders.filter(o => o.status === 'Delivered').length,
    cancelled: orders.filter(o => o.status === 'Cancelled').length,
    inTransit: orders.filter(o => o.status === 'In Transit').length,
    totalSpent: orders
      .filter(o => o.status === 'Delivered')
      .reduce((sum, o) => sum + (o.total || 0), 0)
  };

  return stats;
};

/**
 * Filter orders by status
 * @param {Array} orders - List of orders
 * @param {string} status - Status to filter by
 * @returns {Array} Filtered orders
 */
export const filterOrdersByStatus = (orders, status) => {
  if (!status || status === 'all') {
    return orders;
  }

  return orders.filter(order => 
    order.status.toLowerCase() === status.toLowerCase()
  );
};

/**
 * Filter orders by date range
 * @param {Array} orders - List of orders
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {Array} Filtered orders
 */
export const filterOrdersByDateRange = (orders, startDate, endDate) => {
  return orders.filter(order => {
    const orderDate = new Date(order.date || order.created_at);
    return orderDate >= startDate && orderDate <= endDate;
  });
};

/**
 * Search orders by product name or order ID
 * @param {Array} orders - List of orders
 * @param {string} query - Search query
 * @returns {Array} Filtered orders
 */
export const searchOrders = (orders, query) => {
  if (!query) return orders;

  const lowerQuery = query.toLowerCase();

  return orders.filter(order => {
    // Search by order ID
    if (order.id.toString().includes(query)) {
      return true;
    }

    // Search by product names
    if (order.items && order.items.length > 0) {
      return order.items.some(item =>
        item.name?.toLowerCase().includes(lowerQuery) ||
        item.product_name?.toLowerCase().includes(lowerQuery)
      );
    }

    return false;
  });
};

/**
 * Sort orders
 * @param {Array} orders - List of orders
 * @param {string} sortBy - Sort field (date, total, status)
 * @param {string} order - Sort order (asc, desc)
 * @returns {Array} Sorted orders
 */
export const sortOrders = (orders, sortBy = 'date', order = 'desc') => {
  const sorted = [...orders].sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case 'date':
        aValue = new Date(a.date || a.created_at);
        bValue = new Date(b.date || b.created_at);
        break;
      case 'total':
        aValue = a.total || 0;
        bValue = b.total || 0;
        break;
      case 'status':
        aValue = a.status || '';
        bValue = b.status || '';
        break;
      default:
        aValue = a.id;
        bValue = b.id;
    }

    if (order === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  return sorted;
};

/**
 * Format order date
 * @param {string} dateString - Date string
 * @returns {string} Formatted date
 */
export const formatOrderDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }
};

/**
 * Get order status color
 * @param {string} status - Order status
 * @returns {string} Tailwind color class
 */
export const getOrderStatusColor = (status) => {
  const statusColors = {
    'Pending': 'text-yellow-600 bg-yellow-50',
    'Processing': 'text-blue-600 bg-blue-50',
    'In Transit': 'text-purple-600 bg-purple-50',
    'Delivered': 'text-green-600 bg-green-50',
    'Cancelled': 'text-red-600 bg-red-50',
    'Failed': 'text-red-600 bg-red-50',
    'Confirmed': 'text-blue-600 bg-blue-50'
  };

  return statusColors[status] || 'text-gray-600 bg-gray-50';
};

/**
 * Get order status icon
 * @param {string} status - Order status
 * @returns {string} Icon name
 */
export const getOrderStatusIcon = (status) => {
  const statusIcons = {
    'Pending': 'Clock',
    'Processing': 'Package',
    'In Transit': 'Truck',
    'Delivered': 'CheckCircle',
    'Cancelled': 'XCircle',
    'Failed': 'AlertTriangle',
    'Confirmed': 'Check'
  };

  return statusIcons[status] || 'Package';
};

/**
 * Check if order can be cancelled
 * @param {Object} order - Order object
 * @returns {boolean} Can cancel
 */
export const canCancelOrder = (order) => {
  const cancellableStatuses = ['Pending', 'Processing', 'Confirmed'];
  return cancellableStatuses.includes(order.status);
};

/**
 * Check if order can be rated
 * @param {Object} order - Order object
 * @returns {boolean} Can rate
 */
export const canRateOrder = (order) => {
  return order.status === 'Delivered' && !order.rated;
};

/**
 * Calculate estimated delivery date
 * @param {string} deliverySlot - Delivery slot
 * @returns {string} Estimated delivery date
 */
export const calculateEstimatedDelivery = (deliverySlot) => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const slotTimes = {
    'morning': '6:00 AM - 8:00 AM',
    'afternoon': '2:00 PM - 4:00 PM',
    'evening': '6:00 PM - 8:00 PM'
  };

  const timeRange = slotTimes[deliverySlot] || '6:00 AM - 8:00 PM';

  return `${tomorrow.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short'
  })}, ${timeRange}`;
};

/**
 * Get order summary for checkout
 * @param {Array} cart - Cart items
 * @param {Object} options - Delivery fee, discount, etc.
 * @returns {Object} Order summary
 */
export const getOrderSummary = (cart, options = {}) => {
  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  
  const deliveryFee = options.deliveryFee !== undefined 
    ? options.deliveryFee 
    : (subtotal > 500 ? 0 : 40);
  
  const discount = options.discount || 0;
  const tax = options.tax || 0;
  
  const total = subtotal + deliveryFee + tax - discount;

  return {
    items: cart,
    itemCount,
    subtotal,
    deliveryFee,
    discount,
    tax,
    total,
    savingsMessage: deliveryFee === 0 && subtotal < 500 
      ? `Add ₹${500 - subtotal} more for free delivery!` 
      : null
  };
};

export default {
  placeOrder: createOrder,
  getMyOrders,
  getOrderById,
  trackOrder,
  cancelOrder,
  rateOrder,
  reorder,
  getOrderStats,
  filterOrdersByStatus,
  filterOrdersByDateRange,
  searchOrders,
  sortOrders,
  formatOrderDate,
  getOrderStatusColor,
  getOrderStatusIcon,
  canCancelOrder,
  canRateOrder,
  calculateEstimatedDelivery,
  getOrderSummary
};